import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatCurrency, formatDate, formatRelativeDate, getStatusColor } from '@/lib/utils'
import { format, isToday, isYesterday } from 'date-fns'
import {
  ArrowLeft, Edit3, Check, X, Trash2, DollarSign, User, Calendar,
  Phone, MessageSquare, Award, TrendingUp, Plus, FileText, Building2,
  Briefcase, RotateCcw,
} from 'lucide-react'
import { toast } from 'sonner'
import NewJobModal from '@/components/NewJobModal'
import Avatar from '@/components/Avatar'
import Combobox from '@/components/Combobox'
import TasksThread from '@/components/TasksThread'
import MeetingsThread from '@/components/MeetingsThread'

// ─── Types ────────────────────────────────────────────────────────────────────

type ActivityType =
  | 'created' | 'stage_changed' | 'value_changed' | 'owner_changed'
  | 'won' | 'lost' | 'note' | 'call_logged' | 'meeting_logged' | 'reopened'

interface Activity {
  id: string
  deal_id: string
  workspace_id: string
  type: ActivityType
  payload: Record<string, any>
  created_by?: string
  created_at: string
}

// ─── Small shared components ─────────────────────────────────────────────────

function EditableField({ label, value, onSave, type = 'text', placeholder, prefix }: {
  label: string; value?: string | null; onSave: (v: string) => void;
  type?: string; placeholder?: string; prefix?: string
}) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value || '')
  const handleSave = () => { onSave(val); setEditing(false) }

  if (editing) {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {prefix && <span style={{ fontSize: 13, color: 'var(--ink-tertiary)', flexShrink: 0 }}>{prefix}</span>}
        <input
          autoFocus type={type} value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false) }}
          placeholder={placeholder}
          style={{ flex: 1, minWidth: 0, padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--focus-ring)', borderRadius: 7, fontSize: 13, color: 'var(--ink-primary)', outline: 'none' }}
        />
        <button onClick={handleSave} style={{ padding: '5px 7px', background: 'var(--accent)', border: 'none', borderRadius: 7, cursor: 'pointer', color: 'var(--accent-ink)', display: 'flex', flexShrink: 0 }}>
          <Check style={{ width: 12, height: 12 }} />
        </button>
        <button onClick={() => setEditing(false)} style={{ padding: '5px 7px', background: 'var(--border-subtle)', border: 'none', borderRadius: 7, cursor: 'pointer', color: 'var(--ink-secondary)', display: 'flex', flexShrink: 0 }}>
          <X style={{ width: 12, height: 12 }} />
        </button>
      </div>
    )
  }

  return (
    <div
      onClick={() => { setVal(value || ''); setEditing(true) }}
      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 6px', borderRadius: 7, cursor: 'text', marginLeft: -6 }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      {prefix && <span style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>{prefix}</span>}
      <span style={{ fontSize: 13, color: value ? 'var(--ink-primary)' : 'var(--ink-tertiary)' }}>
        {value || `Add ${label.toLowerCase()}...`}
      </span>
      <Edit3 style={{ width: 11, height: 11, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
    </div>
  )
}

// ─── Activity helpers ─────────────────────────────────────────────────────────

function activityColor(type: ActivityType): string {
  const map: Record<ActivityType, string> = {
    created:        'var(--accent)',
    stage_changed:  'var(--info)',
    value_changed:  'var(--info)',
    owner_changed:  'var(--info)',
    won:            'var(--positive)',
    lost:           'var(--danger)',
    note:           'var(--accent)',
    call_logged:    'var(--info)',
    meeting_logged: 'var(--info)',
    reopened:       'var(--warning)',
  }
  return map[type]
}

function ActivityIcon({ type }: { type: ActivityType }) {
  const s = { width: 14, height: 14 }
  switch (type) {
    case 'created':       return <Plus {...s} />
    case 'stage_changed': return <TrendingUp {...s} />
    case 'value_changed': return <DollarSign {...s} />
    case 'owner_changed': return <User {...s} />
    case 'won':           return <Award {...s} />
    case 'lost':          return <X {...s} />
    case 'note':          return <MessageSquare {...s} />
    case 'call_logged':   return <Phone {...s} />
    case 'meeting_logged':return <Calendar {...s} />
    case 'reopened':      return <RotateCcw {...s} />
  }
}

function activityLabel(a: Activity): string {
  const p = a.payload
  switch (a.type) {
    case 'created':       return 'Deal created'
    case 'stage_changed': return `Stage changed${p.from_stage_name ? ` from ${p.from_stage_name}` : ''} to ${p.to_stage_name || '—'}`
    case 'value_changed': return `Value updated${p.from != null ? ` from ${formatCurrency(p.from)}` : ''} to ${formatCurrency(p.to)}`
    case 'owner_changed': return 'Owner changed'
    case 'won':           return 'Deal marked as won 🎉'
    case 'lost':          return 'Deal marked as lost'
    case 'note':          return 'Added a note'
    case 'call_logged':   return `Logged a call${p.outcome ? ` · ${p.outcome}` : ''}${p.duration_minutes ? ` · ${p.duration_minutes}m` : ''}`
    case 'meeting_logged':return `Logged a meeting${p.when ? ` · ${formatDate(p.when, 'MMM d, h:mm a')}` : ''}`
    case 'reopened':      return 'Deal reopened'
  }
}

function groupByDay(activities: Activity[]): { label: string; items: Activity[] }[] {
  const groups = new Map<string, Activity[]>()
  for (const a of activities) {
    const d = new Date(a.created_at)
    const key = isToday(d) ? 'Today' : isYesterday(d) ? 'Yesterday' : format(d, 'MMM d, yyyy')
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(a)
  }
  return Array.from(groups.entries()).map(([label, items]) => ({ label, items }))
}

// ─── Activity composer ────────────────────────────────────────────────────────

function ActivityComposer({ dealId, workspaceId, onSubmit }: {
  dealId: string; workspaceId: string; onSubmit: () => void
}) {
  const [tab, setTab] = useState<'note' | 'call_logged' | 'meeting_logged'>('note')
  const [noteText, setNoteText] = useState('')
  const [callNotes, setCallNotes] = useState('')
  const [callDuration, setCallDuration] = useState('')
  const [callOutcome, setCallOutcome] = useState('Connected')
  const [meetingNotes, setMeetingNotes] = useState('')
  const [meetingWhen, setMeetingWhen] = useState('')
  const [meetingAttendees, setMeetingAttendees] = useState('')
  const [saving, setSaving] = useState(false)

  const inp: React.CSSProperties = {
    width: '100%', padding: '8px 10px', background: 'var(--field-bg)',
    border: '1px solid var(--border-default)', borderRadius: 8,
    fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box',
  }
  const ta: React.CSSProperties = { ...inp, resize: 'vertical', lineHeight: 1.5, minHeight: 72 }
  const focusBorder = (e: React.FocusEvent<HTMLElement>) => (e.currentTarget.style.borderColor = 'var(--focus-ring)')
  const blurBorder  = (e: React.FocusEvent<HTMLElement>) => (e.currentTarget.style.borderColor = 'var(--border-default)')

  const handleSubmit = async () => {
    if (tab === 'note' && !noteText.trim()) { toast.error('Note text is required'); return }
    setSaving(true)
    let payload: Record<string, any> = {}
    if (tab === 'note') {
      payload = { text: noteText.trim() }
    } else if (tab === 'call_logged') {
      payload = { notes: callNotes, duration_minutes: callDuration ? parseInt(callDuration) : null, outcome: callOutcome }
    } else {
      payload = { notes: meetingNotes, when: meetingWhen || null, attendees: meetingAttendees }
    }
    const { error } = await supabase.from('deal_activities').insert({ deal_id: dealId, workspace_id: workspaceId, type: tab, payload })
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success('Activity logged')
    setNoteText(''); setCallNotes(''); setCallDuration('')
    setMeetingNotes(''); setMeetingWhen(''); setMeetingAttendees('')
    onSubmit()
  }

  const TABS = [
    { id: 'note' as const, label: 'Note' },
    { id: 'call_logged' as const, label: 'Log call' },
    { id: 'meeting_logged' as const, label: 'Log meeting' },
  ]

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 14, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.id ? 600 : 400, color: tab === t.id ? 'var(--ink-primary)' : 'var(--ink-tertiary)', borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent', transition: 'all 150ms ease' }}>
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ padding: '14px 16px' }}>
        {tab === 'note' && (
          <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..."
            style={ta} onFocus={focusBorder} onBlur={blurBorder} />
        )}
        {tab === 'call_logged' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <textarea value={callNotes} onChange={e => setCallNotes(e.target.value)} placeholder="Call notes..."
              style={ta} onFocus={focusBorder} onBlur={blurBorder} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <input type="number" value={callDuration} onChange={e => setCallDuration(e.target.value)}
                placeholder="Duration (min)" style={inp} onFocus={focusBorder} onBlur={blurBorder} />
              <select value={callOutcome} onChange={e => setCallOutcome(e.target.value)}
                style={{ ...inp, cursor: 'pointer' }}>
                {['Connected', 'Voicemail', 'No answer', 'Wrong number'].map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {tab === 'meeting_logged' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <textarea value={meetingNotes} onChange={e => setMeetingNotes(e.target.value)} placeholder="Meeting notes..."
              style={ta} onFocus={focusBorder} onBlur={blurBorder} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {/* TODO Session 3: theme this — colorScheme: 'dark' pinned for native datetime picker icons */}
              <input type="datetime-local" value={meetingWhen} onChange={e => setMeetingWhen(e.target.value)}
                style={{ ...inp, colorScheme: 'dark' }} onFocus={focusBorder} onBlur={blurBorder} />
              <input value={meetingAttendees} onChange={e => setMeetingAttendees(e.target.value)}
                placeholder="Attendees" style={inp} onFocus={focusBorder} onBlur={blurBorder} />
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button onClick={handleSubmit} disabled={saving} className="btn-primary" style={{ padding: '8px 18px' }}>
            {saving ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function DealDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { workspace } = useAuth()
  const queryClient = useQueryClient()

  const { data: deal, isLoading } = useQuery({
    queryKey: ['deal', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('deals').select('*').eq('id', id!).single()
      if (error) throw error
      return data
    },
    enabled: !!id,
  })

  const { data: activities = [] } = useQuery({
    queryKey: ['deal-activities', id],
    queryFn: async () => {
      const { data } = await supabase
        .from('deal_activities')
        .select('*')
        .eq('deal_id', id!)
        .order('created_at', { ascending: false })
      return (data || []) as Activity[]
    },
    enabled: !!id,
  })

  const { data: contact } = useQuery({
    queryKey: ['deal-contact', deal?.contact_id],
    queryFn: async () => {
      const { data } = await supabase.from('contacts').select('*').eq('id', deal!.contact_id!).maybeSingle()
      return data
    },
    enabled: !!deal?.contact_id,
  })

  // Sales Slice 1: the deal's DIRECTLY-attached quotes (by deal_id), not the
  // old contact-shared list. Cast — deal_id isn't in the generated types yet.
  const { data: quotes = [] } = useQuery({
    queryKey: ['deal-quotes', id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('quotes')
        .select('id,quote_number,total,status')
        .eq('deal_id', id!)
        .order('created_at', { ascending: false })
      return data || []
    },
    enabled: !!id,
  })

  // Email auto-capture: the deal's conversations. Gap #3 CP1 — prefer the direct
  // deal_id link, keeping the Slice-1 contact_id traversal as a fallback. The OR
  // dedups inherently (a row matching both clauses returns once). Enabled on the
  // deal id alone so directly-linked threads show even when the deal has no contact.
  const { data: conversations = [] } = useQuery({
    queryKey: ['deal-conversations', deal?.id, deal?.contact_id],
    queryFn: async () => {
      const orFilter = `deal_id.eq.${deal!.id}${deal!.contact_id ? `,contact_id.eq.${deal!.contact_id}` : ''}`
      const { data } = await supabase
        .from('conversations')
        .select('id,subject,status,channel,last_message,last_message_at,created_at,deal_id')
        .or(orFilter)
        .order('created_at', { ascending: false })
        .limit(10)
      return data || []
    },
    enabled: !!deal?.id,
  })

  // Attachable candidates for the "Attach quote" picker: unattached
  // (deal_id IS NULL) quotes in the workspace — never show a quote already on
  // another deal.
  const { data: attachableQuotes = [] } = useQuery({
    queryKey: ['attachable-quotes', workspace?.id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('quotes')
        .select('id,quote_number,contact_name,total,status')
        .eq('workspace_id', workspace!.id)
        .is('deal_id', null)
        .order('created_at', { ascending: false })
      return data || []
    },
    enabled: !!workspace?.id,
  })
  const [quoteQuery, setQuoteQuery] = useState('')

  const attachQuote = useMutation({
    mutationFn: async (quoteId: string) => {
      const { error } = await (supabase as any).from('quotes').update({ deal_id: id }).eq('id', quoteId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-quotes', id] })
      queryClient.invalidateQueries({ queryKey: ['attachable-quotes'] })
      toast.success('Quote attached')
    },
    onError: () => toast.error('Failed to attach quote'),
  })
  const unattachQuote = useMutation({
    mutationFn: async (quoteId: string) => {
      const { error } = await (supabase as any).from('quotes').update({ deal_id: null }).eq('id', quoteId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal-quotes', id] })
      queryClient.invalidateQueries({ queryKey: ['attachable-quotes'] })
      toast.success('Quote detached')
    },
    onError: () => toast.error('Failed to detach quote'),
  })

  const { data: invoices = [] } = useQuery({
    queryKey: ['deal-invoices', deal?.contact_id],
    queryFn: async () => {
      const { data } = await supabase
        .from('invoices')
        .select('id,invoice_number,total,status')
        .eq('contact_id', deal!.contact_id!)
        .order('created_at', { ascending: false })
      return data || []
    },
    enabled: !!deal?.contact_id,
  })

  const { data: profiles = [] } = useQuery({
    queryKey: ['profiles', workspace?.id],
    queryFn: async () => {
      const { data } = await supabase.from('profiles').select('id,full_name').eq('workspace_id', workspace!.id)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  // Multi-pipeline CP4: the stage picker offers only the deal's OWN pipeline's
  // stages. Fallback: a deal with no pipeline_id (pre-CP1 edge) → all workspace
  // stages, so the picker is never empty.
  const { data: stages = [] } = useQuery({
    queryKey: ['pipeline-stages', deal?.pipeline_id || workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      let q = supabase.from('pipeline_stages').select('*').order('order_index')
      q = deal?.pipeline_id ? q.eq('pipeline_id', deal.pipeline_id) : q.eq('workspace_id', workspace.id)
      const { data } = await q
      return data || []
    },
    enabled: !!workspace?.id,
  })

  // Supporting data for Convert-to-Job — query keys/shapes mirror FieldHub's so
  // React Query reuses cache where applicable.
  const { data: jobContacts = [] } = useQuery({
    queryKey: ['contacts', 'typeahead-deal-detail', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('contacts').select('id, first_name, last_name, email, phone, company_name').eq('workspace_id', workspace.id).order('first_name')
      return data || []
    },
    enabled: !!workspace?.id,
  })
  const { data: jobClients = [] } = useQuery({
    queryKey: ['companies-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('companies').select('id, name').eq('workspace_id', workspace.id).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })
  const { data: jobLocations = [] } = useQuery({
    queryKey: ['locations-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('locations').select('id, company_id, name, store_number, address_line1, city, state').eq('workspace_id', workspace.id).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })
  const { data: jobAgents = [] } = useQuery({
    queryKey: ['agents', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles').select('id, full_name, role').eq('workspace_id', workspace.id)
      return data || []
    },
    enabled: !!workspace?.id,
  })
  const { data: jobVendors = [] } = useQuery({
    queryKey: ['vendors-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('vendors').select('id, name, status').eq('workspace_id', workspace.id).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })
  const { data: jobServiceLines = [] } = useQuery({
    queryKey: ['service-lines-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('service_lines').select('id, name, is_active').eq('workspace_id', workspace.id).eq('is_active', true).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const [showConvertModal, setShowConvertModal] = useState(false)

  const profileMap = useMemo(() => {
    const m: Record<string, string> = {}
    profiles.forEach((p: any) => { m[p.id] = p.full_name })
    return m
  }, [profiles])

  const grouped = useMemo(() => groupByDay(activities), [activities])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['deal', id] })
    queryClient.invalidateQueries({ queryKey: ['deals'] })
    queryClient.invalidateQueries({ queryKey: ['deal-activities', id] })
  }

  const updateMutation = useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const { error } = await supabase.from('deals').update(updates).eq('id', id!)
      if (error) throw error
    },
    onSuccess: () => { invalidate(); toast.success('Saved') },
    onError: () => toast.error('Failed to save'),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('deals').delete().eq('id', id!)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      navigate('/app/deals')
      toast.success('Deal deleted')
    },
    onError: () => toast.error('Failed to delete'),
  })

  // Separate from updateMutation so the toast can be 'Deal reopened' rather than
  // the generic 'Saved'. The status flip from won|lost → open fires the
  // log_deal_activity trigger's 'reopened' branch — no client emission needed.
  const reopenMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('deals').update({
        status: 'open', loss_reason: null, loss_reason_details: null,
      }).eq('id', id!)
      if (error) throw error
    },
    onSuccess: () => { invalidate(); toast.success('Deal reopened') },
    onError: () => toast.error('Failed to reopen deal'),
  })

  if (isLoading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'var(--canvas)' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
    </div>
  )

  if (!deal) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-secondary)' }}>Deal not found</div>
  )

  const sc = getStatusColor(deal.status)
  const inp: React.CSSProperties = {
    padding: '5px 8px', background: 'var(--field-bg)',
    border: '1px solid var(--border-default)', borderRadius: 8,
    fontSize: 13, color: 'var(--ink-primary)', outline: 'none',
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      {/* Top bar */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface-raised)' }}>
        <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/app/deals') }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink-primary)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-tertiary)')}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Back
        </button>
        <div style={{ width: 1, height: 16, background: 'var(--border-default)' }} />
        <span style={{ fontSize: 13, color: 'var(--ink-secondary)', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {deal.title}
        </span>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {deal.status === 'open' && (
            <>
              <button onClick={() => updateMutation.mutate({ status: 'won' })}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-faint), transparent)', border: '1px solid var(--positive-border)', borderRadius: 8, color: 'var(--positive)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <Award style={{ width: 12, height: 12 }} /> Mark won
              </button>
              <button onClick={() => updateMutation.mutate({ status: 'lost' })}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)', border: '1px solid var(--danger-border)', borderRadius: 8, color: 'var(--danger)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <X style={{ width: 12, height: 12 }} /> Mark lost
              </button>
            </>
          )}
          {deal.status === 'won' && (
            <button
              onClick={() => setShowConvertModal(true)}
              disabled={!deal.contact_id}
              title={deal.contact_id ? '' : 'Add a contact to this deal before converting to a job'}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: deal.contact_id ? 'var(--accent-dim)' : 'var(--hover-bg)', border: `1px solid ${deal.contact_id ? 'var(--accent-border)' : 'var(--border-subtle)'}`, borderRadius: 8, color: deal.contact_id ? 'var(--accent-text)' : 'var(--ink-tertiary)', fontSize: 12, fontWeight: 600, cursor: deal.contact_id ? 'pointer' : 'not-allowed' }}>
              <Briefcase style={{ width: 12, height: 12 }} /> Convert to Job
            </button>
          )}
          {(deal.status === 'won' || deal.status === 'lost') && (
            <button
              onClick={() => {
                if (!window.confirm("Reopen deal? This will move the deal back to 'open' status. Loss reason will be cleared.")) return
                reopenMutation.mutate()
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <RotateCcw style={{ width: 12, height: 12 }} /> Reopen deal
            </button>
          )}
          <button onClick={() => { if (window.confirm(`Delete "${deal.title}"?`)) deleteMutation.mutate() }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)', border: '1px solid var(--danger-border)', borderRadius: 8, color: 'var(--danger)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <Trash2 style={{ width: 12, height: 12 }} /> Delete
          </button>
        </div>
      </div>

      {/* Header — deal fields */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-raised)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>

          {/* Deal name */}
          <div style={{ flex: '1 1 200px', minWidth: 160 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Deal name</span>
            <EditableField label="Title" value={deal.title}
              onSave={(v: string) => v.trim() && updateMutation.mutate({ title: v.trim() })} />
          </div>

          {/* Stage */}
          <div style={{ minWidth: 130 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Stage</span>
            <select value={deal.stage_id || ''} onChange={e => {
              const s = (stages as any[]).find(s => s.id === e.target.value)
              updateMutation.mutate({ stage_id: e.target.value || null, stage_name: s?.name || null })
            }} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">No stage</option>
              {(stages as any[]).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          {/* Value */}
          <div style={{ minWidth: 120 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Value</span>
            <EditableField label="Value" value={deal.value != null ? deal.value.toString() : ''}
              type="number" prefix="$"
              onSave={(v: string) => updateMutation.mutate({ value: v ? parseFloat(v) : null })} />
          </div>

          {/* Win probability */}
          <div style={{ minWidth: 110 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Probability</span>
            <EditableField label="Probability" value={deal.probability != null ? deal.probability.toString() : ''}
              type="number" placeholder="0–100"
              onSave={(v: string) => updateMutation.mutate({ probability: v ? Math.min(100, Math.max(0, parseInt(v))) : null })} />
          </div>

          {/* Status badge */}
          <div style={{ minWidth: 80 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Status</span>
            <span style={{ display: 'inline-flex', padding: '4px 12px', borderRadius: 100, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>
              {deal.status}
            </span>
          </div>

          {/* Owner */}
          <div style={{ minWidth: 140 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Owner</span>
            <select value={deal.owner_id || ''} onChange={e => {
              const p = (profiles as any[]).find(p => p.id === e.target.value)
              updateMutation.mutate({ owner_id: e.target.value || null, owner_name: p?.full_name || null })
            }} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Unassigned</option>
              {(profiles as any[]).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </div>

          {/* Close date */}
          <div style={{ minWidth: 140 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Close date</span>
            {/* TODO Session 3: theme this — colorScheme: 'dark' pinned for native date picker icons */}
            <input type="date" value={deal.close_date || ''}
              onChange={e => updateMutation.mutate({ close_date: e.target.value || null })}
              style={{ ...inp, colorScheme: 'dark' }}
              onFocus={e => (e.currentTarget.style.borderColor = 'var(--focus-ring)')}
              onBlur={e => (e.currentTarget.style.borderColor = 'var(--border-default)')} />
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

        {/* LEFT: Activity timeline */}
        <div style={{ flex: '0 0 65%', borderRight: '1px solid var(--border-subtle)', overflow: 'auto', padding: '24px' }}>
          {workspace && (
            <ActivityComposer
              dealId={id!}
              workspaceId={workspace.id}
              onSubmit={() => queryClient.invalidateQueries({ queryKey: ['deal-activities', id] })}
            />
          )}

          {activities.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <MessageSquare style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
              <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>No activity yet</p>
            </div>
          ) : (
            <div>
              {grouped.map(group => (
                <div key={group.label} style={{ marginBottom: 24 }}>
                  {/* Day header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                      {group.label}
                    </span>
                    <div style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
                  </div>
                  {/* Entries */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {group.items.map(a => {
                      const color = activityColor(a.type)
                      const actor = a.created_by ? (profileMap[a.created_by] || null) : null
                      return (
                        <motion.div key={a.id}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          style={{ display: 'flex', gap: 12, padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: `color-mix(in srgb, ${color} var(--status-tint-dim), transparent)`, border: `1px solid color-mix(in srgb, ${color} var(--status-tint-strong), transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color }}>
                            <ActivityIcon type={a.type} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                                {actor && (
                                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', flexShrink: 0 }}>{actor}</span>
                                )}
                                <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {activityLabel(a)}
                                </span>
                              </div>
                              <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', flexShrink: 0 }}>
                                {formatRelativeDate(a.created_at)}
                              </span>
                            </div>
                            {a.type === 'note' && a.payload.text && (
                              <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '6px 0 0', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{a.payload.text}</p>
                            )}
                            {a.type === 'call_logged' && a.payload.notes && (
                              <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '6px 0 0', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{a.payload.notes}</p>
                            )}
                            {a.type === 'meeting_logged' && (
                              <>
                                {a.payload.notes && <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '6px 0 0', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{a.payload.notes}</p>}
                                {a.payload.attendees && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>Attendees: {a.payload.attendees}</p>}
                              </>
                            )}
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: Side panel */}
        <div style={{ flex: '0 0 35%', overflow: 'auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Linked contact */}
          <div>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Contact</span>
            {contact ? (
              <div onClick={() => navigate(`/app/contacts/${contact.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer', transition: 'all 150ms ease' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border-subtle)' }}>
                <Avatar name={`${contact.first_name} ${contact.last_name || ''}`.trim()} size={34} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 1px' }}>{contact.first_name} {contact.last_name}</p>
                  {contact.email && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{contact.email}</p>}
                </div>
              </div>
            ) : deal.contact_name ? (
              <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0 }}>{deal.contact_name}</p>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No contact linked</p>
            )}
          </div>

          {/* Conversations — Email auto-capture Slice 1: the contact's email
              threads, surfaced over the existing conversations.contact_id link. */}
          {deal.contact_id && (
            <div>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Conversations</span>
              {conversations.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No emails yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(conversations as any[]).map(conv => {
                    const cs = getStatusColor(conv.status)
                    return (
                      <div key={conv.id} onClick={() => navigate(`/app/inbox/${conv.id}`)}
                        style={{ display: 'flex', gap: 8, padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer', transition: 'all 150ms ease' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border-subtle)' }}>
                        <MessageSquare style={{ width: 14, height: 14, color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.subject || 'Conversation'}</p>
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 100, background: cs.bg, color: cs.text, textTransform: 'capitalize', flexShrink: 0 }}>{conv.status}</span>
                          </div>
                          {conv.last_message && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.last_message}</p>}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 10, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(conv.last_message_at || conv.created_at)}</span>
                            {conv.deal_id === deal.id && (
                              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 100, background: 'var(--accent-dim)', color: 'var(--accent-text)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Linked</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Linked company */}
          {(deal.company_id || deal.contact_name) && (
            <div>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Company</span>
              {contact?.company_name ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
                  <Building2 style={{ width: 15, height: 15, color: 'var(--info)', flexShrink: 0 }} />
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{contact.company_name}</p>
                </div>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No company linked</p>
              )}
            </div>
          )}

          {/* Tags */}
          {deal.tags && deal.tags.length > 0 && (
            <div>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Tags</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {deal.tags.map((tag: string) => (
                  <span key={tag} style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: 'var(--accent-dim)', color: 'var(--accent-text)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)' }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Linked quotes — Sales Slice 1: attached by deal_id (not contact-shared). */}
          <div>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Quotes</span>
            {(quotes as any[]).length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 8px' }}>No quotes attached</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                {(quotes as any[]).map(q => {
                  const qs = getStatusColor(q.status)
                  return (
                    <div key={q.id} onClick={() => navigate(`/app/quotes/${q.id}`)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 9, cursor: 'pointer', transition: 'all 150ms ease' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border-subtle)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <FileText style={{ width: 13, height: 13, color: 'var(--accent-text)' }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', fontFamily: 'monospace' }}>{q.quote_number}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--positive)' }}>{formatCurrency(q.total)}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: qs.bg, color: qs.text, textTransform: 'capitalize' }}>{q.status}</span>
                        {/* Detach (set deal_id = null) — stops row navigation. */}
                        <button type="button" aria-label="Detach quote"
                          onClick={e => { e.stopPropagation(); unattachQuote.mutate(q.id) }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex', alignItems: 'center' }}
                          onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)' }}
                          onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)' }}>
                          <X style={{ width: 12, height: 12 }} />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {/* Attach an existing unattached quote (Combobox over deal_id IS NULL). */}
            <Combobox
              value={null}
              inputValue={quoteQuery}
              onInputChange={setQuoteQuery}
              onChange={(q: any) => { if (q) { attachQuote.mutate(q.id); setQuoteQuery('') } }}
              options={attachableQuotes as any[]}
              getKey={(q: any) => q.id}
              getLabel={(q: any) => q.quote_number}
              renderOption={(q: any) => <span style={{ fontSize: 12 }}>{q.quote_number} · {q.contact_name ?? '—'} · {formatCurrency(q.total)}</span>}
              placeholder="Attach a quote…"
              emptyMessage="No unattached quotes"
            />
          </div>

          {/* Linked invoices */}
          <div>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Invoices</span>
            {(invoices as any[]).length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No invoices</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(invoices as any[]).map(inv => {
                  const is = getStatusColor(inv.status)
                  return (
                    <div key={inv.id} onClick={() => navigate(`/app/invoices/${inv.id}`)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 9, cursor: 'pointer', transition: 'all 150ms ease' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border-subtle)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <FileText style={{ width: 13, height: 13, color: 'var(--positive)' }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', fontFamily: 'monospace' }}>{inv.invoice_number}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--positive)' }}>{formatCurrency(inv.total)}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: is.bg, color: is.text, textTransform: 'capitalize' }}>{inv.status}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Tasks — Sales Slice 2: sales tasks/reminders attached to this deal. */}
          <div>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Tasks</span>
            <TasksThread entityType="deal" entityId={deal.id} variant="compact" />
          </div>

          {/* Meetings — Sales Calendar CP1: scheduled meetings attached to this deal. */}
          <div>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Meetings</span>
            <MeetingsThread entityType="deal" entityId={deal.id} />
          </div>

          {/* Metadata */}
          <div style={{ paddingTop: 8, borderTop: '1px solid var(--border-subtle)' }}>
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '0 0 3px' }}>Created {formatDate(deal.created_at)}</p>
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>Updated {formatRelativeDate(deal.updated_at)}</p>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showConvertModal && (
          <NewJobModal
            onClose={() => setShowConvertModal(false)}
            onSaved={() => {
              setShowConvertModal(false)
              // Both 'Job created' (from NewJobModal) and an invalidation of the
              // deals/jobs caches handle the success surface. No navigation —
              // operator stays on the deal to see the linked-job follow-ups.
              queryClient.invalidateQueries({ queryKey: ['field-jobs-cal'] })
            }}
            contacts={jobContacts}
            agents={jobAgents}
            clients={jobClients}
            locations={jobLocations}
            vendors={jobVendors}
            serviceLines={jobServiceLines}
            defaults={{
              title: deal.title,
              contact_id: deal.contact_id || undefined,
              contact_name: deal.contact_name || undefined,
              company_name: deal.company_name || undefined,
              job_notes: deal.notes || undefined,
              deal_id: deal.id,
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
